import request from 'api/requestUtils'
import config from 'constants/config'

// These endpoints are not yet in the published customer-communication contract.
export interface AlertChannels {
  emailEnabled: boolean
  smsEnabled: boolean
}

interface AlertPreference extends AlertChannels {
  userId: string
  brand: string
  isDefault: boolean
  emailEnabledUpdatedAt: string
  smsEnabledUpdatedAt: string
}

interface DestinationsRemoval {
  userId: string
  brand: string
  destinationsRemoved: number
}

interface FareWatchesRemoval {
  fareWatchesRemoved: number
}

export async function fetchAlertPreferences(userId: string): Promise<AlertChannels> {
  try {
    const response = await request.get<App.ApiResponse<AlertPreference>>(
      `/api/customer-communication/v2/users/${encodeURIComponent(userId)}/preferences/alerts?brand=${config.BRAND}`,
      { credentials: 'include', excludeBrand: true },
    )
    return response.result
  } catch(error) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      return { emailEnabled: true, smsEnabled: true }
    }
    throw error
  }
}

export async function updateAlertPreferences(userId: string, channels: Partial<AlertChannels>): Promise<AlertChannels> {
  const response = await request.put<App.ApiResponse<AlertPreference>, Partial<AlertChannels> & { brand: string }>(
    `/api/customer-communication/v2/users/${encodeURIComponent(userId)}/preferences/alerts`,
    { brand: config.BRAND, ...channels },
    { credentials: 'include', excludeBrand: true },
  )
  return response.result
}

export function enableFareWatchEmailAlerts(userId: string): Promise<AlertChannels> {
  return updateAlertPreferences(userId, { emailEnabled: true })
}

export async function removeAllFareWatches(userId: string): Promise<FareWatchesRemoval> {
  const response = await request.delete<App.ApiResponse<FareWatchesRemoval>>(
    `/api/customer-communication/v1/users/fare-watches?userId=${encodeURIComponent(userId)}&brand=${config.BRAND}`,
    { credentials: 'include', excludeBrand: true },
  )
  return response.result
}

export async function removeAllDestinations(userId: string): Promise<DestinationsRemoval> {
  const response = await request.delete<App.ApiResponse<DestinationsRemoval>>(
    `/api/customer-communication/v2/users/${encodeURIComponent(userId)}/preferences/destinations?brand=${config.BRAND}`,
    { credentials: 'include', excludeBrand: true },
  )
  return response.result
}

export async function removeAllSavedAlerts(userId: string) {
  // Wait for both independent resources, including when one request fails early.
  const [fareWatches, destinations] = await Promise.allSettled([
    removeAllFareWatches(userId),
    removeAllDestinations(userId),
  ])
  return { fareWatches, destinations }
}
